import type { PdpKontext } from '@awf/shared';
import { useId } from 'react';
import stile from './kontext-kasten.module.css';

/** Nur die Ablage wird genannt; "nicht abgelegt" war Rauschen (Rueckmeldung 2026-10-14). */
function sharepointSatz(wert: PdpKontext['sharepoint']): string | null {
  if (wert === 'ja') return 'Nach der Freigabe wird die Seite zusätzlich im SharePoint abgelegt.';
  return null;
}

/**
 * Was der Befund fuer die Person an der Freigabe heisst. Frueher stand nur "Fachliche Pruefung:
 * mit Vorbehalt" mit den Rohsaetzen der Pruefung darunter, und das liess ratlos zurueck
 * (Rueckmeldung 2026-10-13): Titel und Satz sagen jetzt, was zu tun ist.
 */
const AUSSAGE = {
  frei: {
    titel: 'Die fachliche Prüfung hat nichts beanstandet',
    satz: 'Die automatische Prüfung hat die Aussagen der Seite gegen die Produktdaten abgeglichen. Sie können den Text wie gewohnt lesen und freigeben.',
  },
  vorbehalt: {
    titel: 'Bitte lesen Sie den Text vor der Freigabe vollständig',
    // Nur noch, was zu tun ist (Rueckmeldung 2026-10-14): Woher die Hinweise stammen und was
    // schon entfernt wurde, verwirrte mehr, als es half.
    satz: 'Lesen Sie den Text bitte einmal ganz, besonders die markierten Stellen. Passt etwas nicht, ändern Sie es direkt im Text oder hinterlassen Sie eine Anmerkung.',
  },
  offen: {
    titel: 'Der Text wurde nicht automatisch fachlich geprüft',
    satz: 'Bitte prüfen Sie Produktangaben wie Leistungen, Beträge und Tarifnamen selbst, bevor Sie freigeben.',
  },
} as const;

/**
 * Die Saetze der Pruefung kommen roh aus n8n und sprechen Redaktionsjargon ("Lesedurchgang",
 * "person ohne kennzeichnung"). Die bekannten Formen werden ausgeblendet, alles andere bleibt
 * wortgleich stehen. `null` heisst: Der Satz wird nicht angezeigt.
 */
export function alsKlartext(grund: string): string | null {
  // Dass die KI schon Aussagen entfernt hat, ist fuer die Freigabe ohne Belang und verwirrte
  // (Rueckmeldung 2026-10-14): Der Satz entfaellt.
  if (/^Es wurden \d+ unbelegte Aussagen? entfernt/i.test(grund)) return null;
  // Die markierten Stellen nennt schon die Zeile mit den Sprungknoepfen darunter; der Satz
  // dazu wiederholte sie nur in Pruefjargon (Rueckmeldung 2026-10-14).
  if (/^Der Lesedurchgang nach den Streichungen hat \d+ Stellen? markiert/i.test(grund))
    return null;
  return grund;
}

/**
 * Der PDP-Kontext ueber dem Text (Spec 2026-10-12 §5.5): fachlicher Status, Gruende, SharePoint-
 * Wahl. Nur Anzeige -- die SharePoint-Wahl fiel am Entscheidungstor und wird hier nicht geaendert.
 */
export function KontextKasten({
  kontext,
  pruefhinweise = 0,
  onZumHinweis,
}: {
  kontext: PdpKontext;
  /** Offene Pruefhinweise im Text; je einer bekommt einen Sprungknopf. */
  pruefhinweise?: number;
  onZumHinweis?: (index: number) => void;
}) {
  const titelId = useId();
  const zustand =
    kontext.fachlichFreigegeben === true
      ? 'frei'
      : kontext.fachlichFreigegeben === false
        ? 'vorbehalt'
        : 'offen';
  const aussage = AUSSAGE[zustand];
  const ablage = sharepointSatz(kontext.sharepoint);
  const gruende = kontext.gruende.map(alsKlartext).filter((g): g is string => g !== null);
  return (
    <section className={stile.kasten} aria-labelledby={titelId} data-pruefung={zustand}>
      <h2 id={titelId} className={stile.titel}>
        {aussage.titel}
      </h2>
      <p className={stile.satz}>{aussage.satz}</p>
      {gruende.length > 0 && (
        <ul className={stile.gruende} aria-label="Ergebnisse der fachlichen Prüfung">
          {gruende.map((grund, i) => (
            <li key={i}>{grund}</li>
          ))}
        </ul>
      )}
      {/* Direkt zu jedem Hinweis springen: im langen Text gingen sie sonst unter (Rueckmeldung
          2026-10-13). */}
      {pruefhinweise > 0 && onZumHinweis && (
        <div className={stile.sprung} role="group" aria-label="Prüfhinweise im Text">
          <span>
            {pruefhinweise === 1
              ? '1 Prüfhinweis im Text:'
              : `${pruefhinweise} Prüfhinweise im Text:`}
          </span>
          {Array.from({ length: pruefhinweise }, (_, i) => (
            <button
              key={i}
              type="button"
              className={stile.sprungKnopf}
              aria-label={`Zu Prüfhinweis ${i + 1} springen`}
              onClick={() => onZumHinweis(i)}
            >
              Hinweis {i + 1}
            </button>
          ))}
        </div>
      )}
      {ablage && <p className={stile.ablage}>{ablage}</p>}
    </section>
  );
}
